import { isRuntimeV3Member, RUNTIME_V3_PERIOD_GRAINS } from './common';
import type { RuntimeV3PeriodGrain } from './common';
import { RUNTIME_V3_FILTER_OPERATORS } from './query-spec';
import type { RuntimeV3QueryFilter } from './query-spec';

export const RUNTIME_V3_POPULATION_SOURCE_CLASSES = ['recorded', 'modeled', 'unknown'] as const;
export type RuntimeV3PopulationSourceClass = (typeof RUNTIME_V3_POPULATION_SOURCE_CLASSES)[number];

/** One source class for every figure of the population — what the reader is shown. */
export type RuntimeV3UniformSourceComposition = Readonly<{
  kind: 'uniform';
  sourceClass: RuntimeV3PopulationSourceClass;
}>;

/**
 * The source class STATED BY THE PERIOD BUCKET a figure covers (for a
 * fiscal-year revision: the year). Keys are bucket starts at `periodGrain`. It
 * is declared, never inferred, and resolved to a uniform class per figure
 * before a reader sees it; a bucket the declaration does not list, or a figure
 * spanning more than one bucket, resolves to `unknown`, never to a class.
 */
export type RuntimeV3ByPeriodSourceComposition = Readonly<{
  kind: 'by_period';
  periodGrain: RuntimeV3PeriodGrain;
  periods: Readonly<Record<string, RuntimeV3PopulationSourceClass>>;
}>;

export type RuntimeV3SourceComposition =
  | RuntimeV3UniformSourceComposition
  | RuntimeV3ByPeriodSourceComposition;

export type RuntimeV3PopulationSelector =
  | Readonly<{ kind: 'all_rows' }>
  | Readonly<{ kind: 'restricted'; filters: readonly RuntimeV3QueryFilter[] }>;

/** A declared perimeter and its source provenance; neither is an output role. */
export type RuntimeV3Population = Readonly<{
  populationRef: string;
  label: string;
  countingUnit: string;
  includes: readonly string[];
  excludes: readonly string[];
  selector: RuntimeV3PopulationSelector;
  sourceComposition: RuntimeV3SourceComposition;
}>;

function object(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function strings(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every(text);
}

function onlyKeys(value: Readonly<Record<string, unknown>>, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function readByPeriodComposition(
  value: Readonly<Record<string, unknown>>,
): RuntimeV3ByPeriodSourceComposition | null {
  if (!onlyKeys(value, ['kind', 'periodGrain', 'periods']) || !object(value.periods)
    || !isRuntimeV3Member(RUNTIME_V3_PERIOD_GRAINS, value.periodGrain)) return null;
  const entries = Object.entries(value.periods);
  if (entries.length === 0 || !entries.every(([start, sourceClass]) =>
    ISO_DATE.test(start) && isRuntimeV3Member(RUNTIME_V3_POPULATION_SOURCE_CLASSES, sourceClass))) return null;
  return {
    kind: 'by_period', periodGrain: value.periodGrain,
    periods: Object.fromEntries(entries) as Record<string, RuntimeV3PopulationSourceClass>,
  };
}

export function readRuntimeV3SourceComposition(value: unknown): RuntimeV3SourceComposition | null {
  if (!object(value)) return null;
  if (value.kind === 'by_period') return readByPeriodComposition(value);
  if (!onlyKeys(value, ['kind', 'sourceClass'])) return null;
  return value.kind === 'uniform'
    && isRuntimeV3Member(RUNTIME_V3_POPULATION_SOURCE_CLASSES, value.sourceClass)
    ? { kind: 'uniform', sourceClass: value.sourceClass } : null;
}

function filter(value: unknown): value is RuntimeV3QueryFilter {
  return object(value) && onlyKeys(value, ['dimensionRef', 'operator', 'memberRefs'])
    && text(value.dimensionRef) && isRuntimeV3Member(RUNTIME_V3_FILTER_OPERATORS, value.operator)
    && strings(value.memberRefs) && value.memberRefs.length > 0
    && new Set(value.memberRefs).size === value.memberRefs.length;
}

function selector(value: unknown): value is RuntimeV3PopulationSelector {
  if (!object(value)) return false;
  if (value.kind === 'all_rows') return onlyKeys(value, ['kind']);
  return value.kind === 'restricted' && onlyKeys(value, ['kind', 'filters'])
    && Array.isArray(value.filters) && value.filters.length > 0 && value.filters.every(filter);
}

/** Strict form validation; the pinned revision validates the referenced members. */
export function readRuntimeV3Population(value: unknown): RuntimeV3Population | null {
  if (!object(value) || !onlyKeys(value, [
    'populationRef', 'label', 'countingUnit', 'includes', 'excludes', 'selector', 'sourceComposition',
  ])) return null;
  const sourceComposition = readRuntimeV3SourceComposition(value.sourceComposition);
  if (!text(value.populationRef) || !text(value.label) || !text(value.countingUnit)
    || !strings(value.includes) || !strings(value.excludes) || !selector(value.selector)
    || sourceComposition === null) return null;
  return {
    populationRef: value.populationRef, label: value.label, countingUnit: value.countingUnit,
    includes: value.includes, excludes: value.excludes, selector: value.selector, sourceComposition,
  };
}

/** Legacy notes-only artifacts contain no executable population declaration. */
export function readRuntimeV3Populations(payload: unknown): readonly RuntimeV3Population[] | null {
  if (!object(payload) || !Array.isArray(payload.populations) || payload.populations.length === 0) return null;
  const populations = payload.populations.map(readRuntimeV3Population);
  if (populations.some((entry) => entry === null)) return null;
  const valid = populations.filter((entry): entry is RuntimeV3Population => entry !== null);
  return new Set(valid.map((entry) => entry.populationRef)).size === valid.length ? valid : null;
}
